import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useInquiryCategories, categoryLabel } from '../../components/inquiries/inquiryShared';

const ROLES = [
  ['staff', 'Staff', 'Maintain inventory and process online orders'],
  ['manager', 'Manager', 'Everything Staff can do, plus reports and store settings'],
  ['owner', 'Owner', 'Full access, including managing staff logins'],
];
const BLANK_FORM = { username: '', password: '', full_name: '', email: '', role: 'staff', specialties: [], notify_email: true };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // id of user being edited, or 'new'
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const categories = useInquiryCategories();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = () => api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing('new'); setForm({ ...BLANK_FORM }); setMsg(''); };
  const startEdit = (u) => { setEditing(u.id); setForm({ full_name: u.full_name, email: u.email || '', role: u.role, new_password: '', specialties: u.specialties || [], notify_email: !!u.notify_email }); setMsg(''); };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      if (editing === 'new') {
        // No specialties picked → server applies the team's defaults if the name matches
        await api.post('/admin/users', { ...form, specialties: form.specialties.length ? form.specialties : undefined });
      } else {
        await api.put(`/admin/users/${editing}`, form);
      }
      setEditing(null);
      setMsg('Saved!');
      load();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const setActive = async (u, active) => {
    setMsg('');
    try {
      await api.put(`/admin/users/${u.id}`, { active });
      load();
    } catch (e) { setMsg(e.response?.data?.error || 'Update failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Staff Logins</h1>
          <p style={{ color: '#9a9a9a', fontSize: 13 }}>Create admin accounts for employees to manage inventory, orders, and reports</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={startNew}>+ Add Staff Login</button>
      </div>
      {msg && <div className={`alert ${msg === 'Saved!' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 16 }}>{msg}</div>}

      {editing === 'new' && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <UserForm form={form} set={set} categories={categories} isNew onCancel={() => setEditing(null)} onSave={save} saving={saving} />
        </div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {users.map(u => (
          <div key={u.id} className="card" style={{ padding: 20, opacity: u.active ? 1 : 0.6 }}>
            {editing === u.id ? (
              <UserForm form={form} set={set} categories={categories} onCancel={() => setEditing(null)} onSave={save} saving={saving} />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#1a2744', fontSize: 15 }}>
                    {u.full_name} {!u.active && <span style={{ fontSize: 11, fontWeight: 700, color: '#c62828', marginLeft: 6 }}>DEACTIVATED</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>@{u.username} {u.email ? `· ${u.email}` : ''}</div>
                  <div style={{ fontSize: 11, color: '#c9a227', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4 }}>{u.role}{!u.notify_email && <span style={{ color: '#9a9a9a', marginLeft: 8 }}>· email alerts off</span>}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {u.specialties?.length
                      ? u.specialties.map(k => <span key={k} className="badge badge-gray" style={{ fontSize: 10 }}>{categoryLabel(categories, k)}</span>)
                      : <span style={{ fontSize: 11, color: '#9a9a9a' }}>No specialties — gets only inquiries nobody else covers</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => startEdit(u)}>Edit</button>
                  {u.active ? (
                    <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => setActive(u, false)}>Deactivate</button>
                  ) : (
                    <button className="btn btn-ghost btn-sm" style={{ color: '#2e7d32' }} onClick={() => setActive(u, true)}>Reactivate</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function UserForm({ form, set, categories, isNew, onCancel, onSave, saving }) {
  const toggle = (key) => set('specialties', form.specialties.includes(key) ? form.specialties.filter(k => k !== key) : [...form.specialties, key]);
  return (
    <div>
      <div className="grid-2" style={{ gap: 16 }}>
        {isNew && (
          <div className="form-group"><label className="form-label">Username</label><input className="form-control" value={form.username} onChange={e => set('username', e.target.value)} /></div>
        )}
        <div className="form-group"><label className="form-label">Full Name</label><input className="form-control" value={form.full_name} onChange={e => set('full_name', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
            {ROLES.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">{isNew ? 'Password' : 'Reset Password (optional)'}</label>
          <input className="form-control" type="password" placeholder={isNew ? '' : 'Leave blank to keep current password'}
            value={isNew ? form.password : (form.new_password || '')}
            onChange={e => set(isNew ? 'password' : 'new_password', e.target.value)} />
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#9a9a9a', margin: '4px 0 16px' }}>
        {ROLES.find(([val]) => val === form.role)?.[2]}
      </p>
      <div className="form-group">
        <label className="form-label">Inquiry specialties</label>
        <p style={{ fontSize: 12, color: '#9a9a9a', marginTop: -2, marginBottom: 8 }}>New inquiries in these categories alert this person. Categories nobody covers go to everyone.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
          {categories.filter(c => c.key !== 'other').map(c => (
            <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#444', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.specialties.includes(c.key)} onChange={() => toggle(c.key)} /> {c.label}
            </label>
          ))}
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#444', marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={!!form.notify_email} onChange={e => set('notify_email', e.target.checked)} />
        Email this person about their inquiries {!form.email && <span style={{ color: '#e65100' }}>(add an email above)</span>}
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}
